import React, { useState, useEffect, useContext } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import API from '../../services/API'
import {
  Paper,
  Typography,
  Button,
  Box,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { GlobalContext } from '../../App'
import SuccessModal from '../../components/Modals/Success'
import SmallModal from '../../components/Modals/SmallModal'
import EmployeeSelector from '../events/EmployeeSelector'

const isUnicode = (text) => {
  const gsmRegex =
    /^[\x00-\x7F€£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞ{}\\[\]~|@!"#$%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà\sA-Za-z0-9^]*$/
  return !gsmRegex.test(text)
}

const getSmsInfo = (text) => {
  const unicode = isUnicode(text)
  const perPart = unicode ? 67 : 153
  const singleLimit = unicode ? 70 : 160
  const maxParts = 8
  const maxLength = unicode ? 536 : 1224

  const length = text.length
  const parts = length <= singleLimit ? 1 : Math.ceil(length / perPart)
  const remaining = maxLength - length

  return {
    unicode,
    creditsUsed: Math.min(parts, maxParts),
    remainingChars: remaining > 0 ? remaining : 0,
    maxLength,
  }
}

const OutboundSMS = ({ passedEmployees, onClose, eventLink }) => {
  const [employeeOptions, setEmployeeOptions] = useState([])
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const [message, setMessage] = useState('')
  const [link, setLink] = useState('none')
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [sending, setSending] = useState(false)
  const creditsUsed = Math.ceil(message.length / 160)
  const smsInfo = getSmsInfo(message)
  const [openLinkPicker, setOpenLinkPicker] = useState(false)

  const [remainingCredits, setRemainingCredits] = useState(0)
  const notEnoughCredits =
    remainingCredits < creditsUsed * selectedEmployees.length

  useEffect(() => {
    API.get('/settings?select=smsCredits').then((res) =>
      setRemainingCredits(res.data.smsCredits),
    )
  }, [])

  useEffect(() => {
    if (passedEmployees)
      setSelectedEmployees(
        employeeOptions.filter((x) => passedEmployees.includes(x._id)),
      )
  }, [passedEmployees, employeeOptions])

  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    API.get('/sms-recipients')
      .then((res) => {
        setEmployeeOptions(res.data)
      })
      .catch((e) => console.log(e))
  }, [])

  const handleSend = (e) => {
    e.preventDefault()
    setSending(true)
    API.post('/sendSMS', {
      recipients: selectedEmployees.reduce((accumulator, employee) => {
        accumulator.push({
          number: employee.employee.personnelInfo.phoneNumber1,
          userID: employee._id,
        })
        return accumulator
      }, []),
      message,
      eventLink,
    })
      .then((res) => {
        if (res.status === 200) {
          // setToast({ message: 'SMS Successfully Sent', severity: 'success' })
          // setSelectedEmployees([])
          // setMessage('')
          // setSubmitSuccess(true)
          onClose ? onClose() : setSubmitSuccess(true)
        } else {
          setToast({
            message: 'Unable to send SMS. Please try again later',
            severity: 'error',
          })
          setSending(false)
        }
      })
      .catch((e) => console.log(e))
  }

  const currentURL = window.location.origin

  const predefinedLinks = {
    'Available Shifts': `${currentURL}/available-shifts`,
    Announcements: `${currentURL}/announcements`,
    'Policy Library': `${currentURL}/policy-library`,
    'Crew Vault': `${currentURL}/crewvault`,
    'SOPs & Guidelines': `${currentURL}/standard-operating-procedures`,
    'PGD Library': `${currentURL}/pgd-library`,
    'PGD Library': `${currentURL}/pgd-library`,
    'Mandatory Training': `${currentURL}/mandatory-training`,
  }

  const handleLinkSelect = (label) => {
    const url = predefinedLinks[label]
    const linkText = `\n\n${label}: ${url}`
    const newMessage = message + linkText

    if (newMessage.length <= getSmsInfo(newMessage).maxLength) {
      setMessage(newMessage)
      setOpenLinkPicker(false)
    } else {
      setToast({
        message: 'Adding this link would exceed the SMS character limit.',
        severity: 'warning',
      })
    }
  }

  return (
    <Box component="form" onSubmit={handleSend}>
      <Stack component={Paper} padding={1} gap={2}>
        <Typography variant="title2">Send SMS</Typography>
        <EmployeeSelector
          value={selectedEmployees}
          onChange={(e, newValue) => setSelectedEmployees(newValue)}
          fullWidth
          multiple
          additionalSelectors="personnelInfo.phoneNumber1"
          label="Recipients"
          getOptionDisabled={(option) =>
            !option?.employee?.personnelInfo?.phoneNumber1
          }
          renderOption={(props, option) => {
            const { key, ...rest } = props
            return (
              <Box key={key} display="flex" gap={1} {...rest}>
                <Avatar
                  src={option?.photograph}
                  sx={{ width: 24, height: 24 }}
                />
                <Typography>
                  {option?.firstName} {option?.lastName}
                  {option?.employee?.clinicalRole
                    ? ` (${option.employee.clinicalRole})`
                    : ''}
                </Typography>
                {!option?.employee?.personnelInfo?.phoneNumber1
                  ? '(No Phone Number)'
                  : ''}
              </Box>
            )
          }}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option
            return `${option.firstName || ''} ${option.lastName || ''}${
              option?.employee?.clinicalRole
                ? ` (${option.employee.clinicalRole})`
                : ''
            } ${!option?.employee?.personnelInfo?.phoneNumber1 ? ' (No Phone Number)' : ''}`
          }}
        />
        <TextField
          multiline
          minRows={2}
          value={message}
          onChange={(e) => {
            const newText = e.target.value
            if (newText.length <= smsInfo.maxLength) {
              setMessage(newText)
            }
          }}
          label="Message"
          helperText={`Credit per SMS: ${smsInfo.creditsUsed}. Remaining Characters: ${smsInfo.remainingChars}`}
          inputProps={{ maxLength: smsInfo.maxLength }}
        />
        <Button
          onClick={() => setOpenLinkPicker(true)}
          size="small"
          variant="outlined"
          color="anPurple"
        >
          Insert Link
        </Button>
        <SmallModal
          autoWidth
          open={openLinkPicker}
          onClose={() => setOpenLinkPicker(false)}
        >
          <Stack>
            <Typography variant="title3">Select a Link:</Typography>
            {Object.keys(predefinedLinks).map((label, index) => (
              <Box
                key={label}
                onClick={() => handleLinkSelect(label)}
                sx={{ cursor: 'pointer' }}
                bgcolor={index % 2 ? '#F5F5F5' : '#E8E8E8'}
                padding={0.5}
              >
                <Typography variant="h5" fontSize="1rem">
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SmallModal>
        {notEnoughCredits && (
          <Alert severity="error">
            <AlertTitle>Not Enough Credits</AlertTitle>
            Sorry, you do not have enough credits to send this message. You have{' '}
            {remainingCredits} SMS credits left.
          </Alert>
        )}
        <Button
          type="submit"
          disabled={
            !message ||
            selectedEmployees.length <= 0 ||
            sending ||
            notEnoughCredits
          }
          variant="contained"
          endIcon={sending ? <CircularProgress size="1rem" /> : <SendIcon />}
        >
          Send
        </Button>
      </Stack>
      <SuccessModal
        open={submitSuccess}
        newUrl="/sms"
        message="SMS Successfully Sent!"
      />
    </Box>
  )
}

export default OutboundSMS
